import {
   GenericSearchResultItem,
   ProductSearchResultItem,
   NewsSearchResultItem,
   DeviceSearchResultItem,
   GuideSearchResultItem,
   SearchResultItem,
   PostSearchResultItem,
} from '../../types';
import { usePriceTier } from '@ifixit/app/hooks';
import { Highlight } from '../highlight';
import { useTranslations } from '@ifixit/i18n';
import { type LocaleCode } from '@ifixit/i18n';
import { type Doctype } from '@ifixit/helpers';
import { determineProductPrice } from '../../utils/price-helpers';
import { FaIcon } from '../../../components/icon';
import { Badge } from '@ifixit/ui/components/badge';

/**
 * Formats a timestamp into a localized date string
 */
function formatTimestampToDate(timestamp: number): string {
   if (!timestamp) return '';
   const date = new Date(timestamp * 1000);
   return date.toLocaleDateString();
}

/**
 * News result component
 */
function NewsResult({ hit }: { hit: NewsSearchResultItem }) {
   return (
      <div className="flex flex-col items-start gap-0.5 self-stretch">
         <div className="self-stretch text-gray-900 font-medium text-sm">
            <Highlight attribute="post_title" hit={hit} />
         </div>
         {hit.post_date && (
            <div className="text-gray-600 text-xs">
               {formatTimestampToDate(hit.post_date as number)}
            </div>
         )}
         {hit.content && (
            <div className="text-gray-800 text-sm mt-1">
               <Highlight attribute="content" hit={hit} isSnippet={true} />
            </div>
         )}
      </div>
   );
}

/**
 * Determines if a guide should be marked as featured
 */
function isGuideFeatured(hit: GuideSearchResultItem): boolean {
   return !!(hit.flags && hit.flags.includes('GUIDE_STARRED'));
}

/**
 * Guide result component
 */
function GuideResult({ hit }: { hit: GuideSearchResultItem }) {
   const isFeatured = isGuideFeatured(hit);
   const t = useTranslations('Search.cards');

   return (
      <div className="flex flex-col items-start gap-0.5 self-stretch">
         <div className="flex justify-between w-full items-start">
            <div className="text-gray-900 font-medium text-sm">
               <Highlight attribute="title" hit={hit} />
            </div>
            {isFeatured && <Badge className="badge-brand badge-md">{t('featured')}</Badge>}
         </div>
         {hit.content && (
            <div className="text-gray-800 text-sm mt-1">
               <Highlight attribute="content" hit={hit} isSnippet={true} />
            </div>
         )}
      </div>
   );
}

/**
 * Post result component
 */
function PostResult({ hit }: { hit: PostSearchResultItem }) {
   const isAccepted = hit.accepted === true;
   const t = useTranslations('Search.cards');

   return (
      <div className="flex flex-col items-start gap-0.5 self-stretch">
         <div className="flex justify-between w-full items-start">
            <div className="text-gray-900 font-medium text-sm">
               <Highlight attribute="title" hit={hit} />
            </div>
            {isAccepted && <Badge className="badge-green badge-md">{t('accepted')}</Badge>}
            {Boolean(hit.is_discussion) && (
               <Badge className="badge-brand badge-md">{t('discussion')}</Badge>
            )}
         </div>
         {hit.description && (
            <div className="text-gray-800 text-sm">
               <Highlight attribute="description" hit={hit} isSnippet={true} />
            </div>
         )}
      </div>
   );
}

/**
 * Product result component showing price info
 */
function ProductResult({
   hit,
   currency,
   localeCode,
   storeCode,
}: {
   hit: ProductSearchResultItem;
   currency: string;
   localeCode: LocaleCode;
   storeCode: string;
}) {
   const { priceTier, isProUser } = usePriceTier({ storeCode });
   const { formattedPrice, isDiscounted, formattedOriginalPrice, discountPercent, isRange } =
      determineProductPrice(hit, priceTier, currency, localeCode);
   const t = useTranslations('components.badges.DiscountBadge');

   return (
      <div className="flex grow w-full">
         <div className="flex flex-col items-start gap-0.5 self-stretch">
            <div className="flex items-center gap-2 self-stretch text-gray-900 font-medium text-sm">
               <Highlight attribute="title" hit={hit} />
            </div>
            <div className="flex gap-2 items-center">
               {isDiscounted && isProUser && (
                  <FaIcon name="rectangle-pro" className="text-amber-500 size-7" />
               )}
               {formattedPrice && (
                  <div className="text-gray-800 text-sm leading-base">{formattedPrice}</div>
               )}
               {isDiscounted && !isRange && (
                  <div className="text-gray-500 text-sm line-through">{formattedOriginalPrice}</div>
               )}
            </div>
         </div>
         <div className="flex gap-2 justify-end flex-grow items-start">
            {discountPercent > 0 && (
               <Badge className={`badge-md ${isProUser ? 'badge-amber' : 'badge-red'}`}>
                  {t('label', { percentage: discountPercent })}
               </Badge>
            )}
            {hit.is_pro === 1 && <Badge className="badge-amber badge-md">Pro Only</Badge>}
         </div>
      </div>
   );
}

/**
 * Device result component
 */
function DeviceResult({ hit }: { hit: DeviceSearchResultItem }) {
   return (
      <div className="flex flex-col items-start gap-0.5 self-stretch text-gray-900 font-medium text-sm">
         <Highlight attribute="title" hit={hit} />
      </div>
   );
}

/**
 * Generic result content component
 */
function GenericResult({ hit }: { hit: GenericSearchResultItem }) {
   return (
      <div className="flex flex-col items-start gap-0.5 self-stretch">
         <div className="self-stretch text-gray-900 font-medium text-sm">
            <Highlight attribute="title" hit={hit} />
         </div>
         {hit.description && (
            <div className="text-gray-800 text-sm">
               <Highlight attribute="description" hit={hit} isSnippet={true} />
            </div>
         )}
      </div>
   );
}

/**
 * Renders the appropriate result component based on doctype
 */
export function ResultContent({
   hit,
   currency,
   localeCode,
   storeCode,
}: {
   hit: SearchResultItem;
   currency: string;
   localeCode: LocaleCode;
   storeCode: string;
}) {
   switch (hit.doctype as Doctype) {
      case 'product': {
         return (
            <ProductResult
               hit={hit as ProductSearchResultItem}
               currency={currency}
               localeCode={localeCode}
               storeCode={storeCode}
            />
         );
      }
      case 'topic': {
         return <DeviceResult hit={hit as DeviceSearchResultItem} />;
      }
      case 'news': {
         return <NewsResult hit={hit as NewsSearchResultItem} />;
      }
      case 'guide': {
         return <GuideResult hit={hit as GuideSearchResultItem} />;
      }
      case 'post': {
         return <PostResult hit={hit as PostSearchResultItem} />;
      }
      default: {
         return <GenericResult hit={hit as GenericSearchResultItem} />;
      }
   }
}
